import React, { useState, useEffect } from 'react';
import {
  Container,
  Form,
  Button,
  Alert,
  Spinner,
  Card,
  Row,
  Col,
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSave, FaTimes, FaListUl, FaTag } from 'react-icons/fa';

import {
  useGetCategoryByIdQuery,
  useUpdateCategoryMutation,
} from '../slices/categoryApiSlice';

const EditCategoryScreen = () => {
  const { id: categoryId } = useParams();
  const navigate = useNavigate();

  const { data: category, isLoading, error } = useGetCategoryByIdQuery(categoryId);
  const [updateCategory, { isLoading: updating }] = useUpdateCategoryMutation();

  const [name, setName] = useState('');
  const [subcategories, setSubcategories] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (category) {
      setName(category.name);
      setSubcategories(category.subcategories.join(', '));
    }
  }, [category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');

    try {
      await updateCategory({
        id: categoryId,
        name,
        subcategories: subcategories
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }).unwrap();

      setSuccessMessage('Category updated successfully!');
      setTimeout(() => navigate('/admin/categories'), 1500);
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const handleCancel = () => {
    navigate('/admin/categories');
  };

  const getErrorMessage = () => {
    if (error?.status === 404) return 'Category not found';
    return error?.data?.message || 'Something went wrong';
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={7}>
          <Card className="shadow-lg p-4 border-0" style={{ borderRadius: '12px' }}>
            <h2 className="text-center mb-4 fw-bold" style={{ color: '#0d6efd' }}>
              <FaTag className="me-2 mb-1" />
              Edit Category
            </h2>

            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : error ? (
              <Alert variant="danger">{getErrorMessage()}</Alert>
            ) : (
              <Form onSubmit={handleSubmit}>
                {successMessage && (
                  <Alert variant="success">{successMessage}</Alert>
                )}

                {/* Category Name */}
                <Form.Group controlId="name" className="mb-3">
                  <Form.Label className="fw-semibold">
                    <FaTag className="me-2 text-primary" /> Category Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter category name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="shadow-sm"
                  />
                </Form.Group>

                {/* Subcategories */}
                <Form.Group controlId="subcategories" className="mb-3">
                  <Form.Label className="fw-semibold">
                    <FaListUl className="me-2 text-success" /> Subcategories
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Comma-separated list"
                    value={subcategories}
                    onChange={(e) => setSubcategories(e.target.value)}
                    className="shadow-sm"
                  />
                  <Form.Text className="text-muted">
                    Example: Fiction, Mystery, Sci-Fi
                  </Form.Text>
                </Form.Group>

                {/* Buttons */}
                <div className="d-flex flex-wrap justify-content-between mt-4">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="px-4 py-2 mb-2"
                    onClick={handleCancel}
                  >
                    <FaTimes className="me-2" />
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    className="px-4 py-2 mb-2"
                    disabled={updating}
                  >
                    {updating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Update Category
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EditCategoryScreen;
